import { getInitials } from '@choco-admin/utils';
import { Avatar, Chip, TableRow, TableCell } from '@heroui/react';
import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { FiPlus, FiPackage, FiGlobe } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';

import {
  PageHeader,
  type PageHeaderAction,
} from '../../../../components/PageHeader';
import {
  DataTablePagination,
  LoadingTable,
  TableHeader as CustomTableHeader,
} from '../../../../components/Table';
import { getSupplierListEmails } from '../../utils/supplierEmails';

import { AccessPackagePills } from './components/AccessPackagePills';
import { ErrorState } from './components/ErrorState';
import { PAGE_SIZE, LOADING_TABLE_COLUMNS } from './constants';
import { useGetSuppliersQuery } from './hooks/useGetSuppliersQuery';
import { type Supplier } from './types';

export const SupplierList: React.FC = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [before, setBefore] = useState('');
  const [after, setAfter] = useState('');

  const { suppliers, total, isLoading, isFetching, error, refetch, pageInfo } =
    useGetSuppliersQuery({
      searchTerm,
      pageSize: PAGE_SIZE,
      before,
      after,
    });

  const pageActions: PageHeaderAction[] = [
    {
      label: 'Create Distributor',
      icon: <FiPlus size={16} />,
      onClick: () => navigate('/suppliers/create'),
      color: 'primary',
    },
  ];

  const handleRowClick = (supplierId: string) => {
    navigate(`/suppliers/${supplierId}`);
  };

  const handlePageChange = (page: number) => {
    if (page > currentPage) {
      setBefore('');
      setAfter(pageInfo.endCursor ?? '');
      setCurrentPage(page);
    } else if (page < currentPage) {
      setAfter('');
      setBefore(pageInfo.startCursor ?? '');
      setCurrentPage(page);
    }
  };

  const handleSearch = (value: string) => {
    setSearchTerm(value);
    setCurrentPage(1);
    setBefore('');
    setAfter('');
  };

  const renderContent = () => {
    // Show error state, but keep the search header mounted so typing
    // doesn't get interrupted if a retry starts loading again.
    if (error) {
      return (
        <div className="space-y-6">
          <CustomTableHeader
            searchValue={searchTerm}
            onSearchChange={handleSearch}
            searchPlaceholder="Search distributors by name or reference ID..."
            isLoading={isLoading}
          />
          <ErrorState error={error as Error} onRetry={refetch} />
        </div>
      );
    }

    return (
      <>
        <CustomTableHeader
          searchValue={searchTerm}
          onSearchChange={handleSearch}
          searchPlaceholder="Search distributors by name or reference ID..."
          isLoading={isLoading}
        >
          <div className="text-sm text-default-600">
            {isLoading ? (
              <span className="font-medium">Loading...</span>
            ) : (
              <>
                <span className="font-medium">Page {currentPage}</span>
                <span className="text-default-400 mx-2">•</span>
                <span>
                  {suppliers.length} of {total} distributors
                </span>
                {isFetching && (
                  <span className="ml-2 text-primary-500">(updating...)</span>
                )}
              </>
            )}
          </div>
        </CustomTableHeader>

        <LoadingTable
          columns={LOADING_TABLE_COLUMNS}
          rowCount={5}
          ariaLabel="Distributors table"
          loadingMessage="Loading distributors..."
          isLoading={isLoading}
          isEmpty={!isLoading && suppliers.length === 0}
          data={suppliers}
          renderRow={(supplier: Supplier) => (
            <TableRow
              key={supplier.id}
              onClick={() => handleRowClick(supplier.id)}
              className="cursor-pointer hover:bg-default-100 transition-colors"
            >
              <TableCell className="pl-6">
                <div className="flex items-center gap-3">
                  <Avatar
                    src={
                      supplier.botUser?.profileImage?.mediumSquare || undefined
                    }
                    name={getInitials(supplier.name || 'S')}
                    size="md"
                    className="flex-shrink-0"
                  />
                  <div className="flex flex-col min-w-0">
                    <span className="font-medium text-foreground truncate">
                      {supplier.name}
                    </span>
                    <span className="text-sm text-default-500 truncate">
                      {getSupplierListEmails(supplier) ?? 'No contact email'}
                    </span>
                  </div>
                </div>
              </TableCell>
              <TableCell className="hidden md:table-cell">
                <Chip
                  variant="bordered"
                  color="default"
                  size="sm"
                  classNames={{
                    base: 'bg-default-100 text-default-700 p-2 select-text cursor-text',
                    content: 'font-mono text-xs select-text',
                  }}
                >
                  {supplier.referenceId || 'N/A'}
                </Chip>
              </TableCell>
              <TableCell className="hidden lg:table-cell">
                <AccessPackagePills
                  accessPackage={supplier.accessPackage || 'free'}
                  isIntegrated={supplier.isIntegrated || false}
                />
              </TableCell>
              <TableCell className="hidden lg:table-cell">
                <div className="flex items-center gap-1 text-sm text-default-600">
                  <FiGlobe size={14} className="text-default-400" />
                  <span className="select-text cursor-text">
                    {supplier.locale || 'N/A'}
                  </span>
                </div>
              </TableCell>
            </TableRow>
          )}
          emptyState={{
            icon: FiPackage,
            iconBgClass: 'bg-primary-50',
            iconColorClass: 'text-primary-500',
            title:
              total > 0
                ? 'No distributors match your search'
                : 'No distributors found',
            description:
              total > 0
                ? "Try adjusting your search term to find the distributors you're looking for."
                : 'Get started by adding your first distributor to the system.',
            action:
              total === 0
                ? {
                    label: 'Add First Distributor',
                    onClick: () => navigate('/suppliers/create'),
                    icon: FiPlus,
                  }
                : undefined,
          }}
        />

        <DataTablePagination
          totalItems={total}
          currentPage={currentPage}
          pageSize={PAGE_SIZE}
          totalPages={Math.ceil(total / PAGE_SIZE)}
          onPageChange={handlePageChange}
          itemName="distributors"
          isLoading={isLoading}
        />
      </>
    );
  };

  return (
    <>
      <Helmet>
        <title>Distributor List - Choco Admin</title>
      </Helmet>
      <div className="p-6 min-h-screen bg-background">
        <PageHeader
          title="Distributor List"
          description="Manage distributor accounts and relationships"
          actions={pageActions}
        />

        <div className="space-y-6">{renderContent()}</div>
      </div>
    </>
  );
};
